import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertIcon,
  Box,
  Card,
  CardBody,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  Spinner,
  Stack,
  Text,
  VStack,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { useParams } from "react-router-dom";
import { getNetworkReportOrganizations } from "../networkReportApi";
import {
  buildNetworkPairingGroups,
  filterNetworkPairingGroups,
} from "../networkReportPairingGroups";
import NetworkPairingModeSelector from "../components/NetworkPairingModeSelector";
import NetworkPairingFactorGroup from "../components/NetworkPairingFactorGroup";

const modeCopy = {
  development: {
    subtitle: "Schools grouped according to common development needs",
    empty: "No shared development areas are available for this Network.",
  },
  strength: {
    subtitle: "Schools grouped according to common strengths",
    empty: "No shared strengths are available for this Network.",
  },
};

const NetworkReportPairingTab = () => {
  const { networkId } = useParams();
  const toast = useToast();
  const mutedColor = useColorModeValue("gray.600", "gray.400");
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [mode, setMode] = useState("development");
  const [searchText, setSearchText] = useState("");

  useEffect(() => {
    let active = true;

    const loadOrganizations = async () => {
      setLoading(true);
      setError("");
      setSearchText("");
      setOrganizations([]);
      try {
        const result = await getNetworkReportOrganizations(networkId);
        if (active) setOrganizations(result);
      } catch (err) {
        console.error("Error loading Network Pairing data:", err);
        if (active) {
          setError("Could not load Network Pairing data.");
          toast({
            title: "Could not load Network Pairing data",
            status: "error",
            duration: 4000,
            isClosable: true,
          });
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    loadOrganizations();
    return () => {
      active = false;
    };
  }, [networkId, toast]);

  const pairing = useMemo(
    () => buildNetworkPairingGroups(organizations, mode),
    [organizations, mode]
  );
  const filteredGroups = useMemo(
    () => filterNetworkPairingGroups(pairing.groups, searchText),
    [pairing.groups, searchText]
  );
  const hasSearch = Boolean(searchText.trim());

  if (loading) {
    return (
      <HStack justify="center" py={12}>
        <Spinner size="lg" label="Loading Network Pairing data" />
      </HStack>
    );
  }

  if (error) {
    return <Alert status="error" borderRadius="md"><AlertIcon />{error}</Alert>;
  }

  return (
    <VStack align="stretch" spacing={5}>
      <Box>
        <Heading size="md">Pairing</Heading>
        <Text mt={1} color={mutedColor}>
          {modeCopy[mode].subtitle}
        </Text>
      </Box>

      {!pairing.organizationCount ? (
        <Card bg={cardBg} borderWidth="1px" borderColor={borderColor}>
          <CardBody>
            <Heading size="sm">No schools</Heading>
            <Text mt={2} color={mutedColor}>
              No schools are available in this Network yet.
            </Text>
          </CardBody>
        </Card>
      ) : (
        <>
          <Card bg={cardBg} borderWidth="1px" borderColor={borderColor}>
            <CardBody>
              <NetworkPairingModeSelector mode={mode} onChange={setMode} />
              <Stack
                direction={{ base: "column", md: "row" }}
                align={{ base: "stretch", md: "end" }}
                justify="space-between"
                spacing={4}
                mt={4}
              >
                <FormControl maxW={{ md: "360px" }}>
                  <FormLabel mb={1}>Search</FormLabel>
                  <Input
                    value={searchText}
                    onChange={(event) => setSearchText(event.target.value)}
                    placeholder="Search factors or schools"
                  />
                </FormControl>
                <Text color={mutedColor} fontSize="sm" aria-live="polite">
                  {pairing.organizationsWithReportData} school
                  {pairing.organizationsWithReportData === 1 ? "" : "s"} with report data
                </Text>
              </Stack>
            </CardBody>
          </Card>

          {!pairing.groups.length ? (
            <Alert status="info" borderRadius="md">
              <AlertIcon />{modeCopy[mode].empty}
            </Alert>
          ) : !filteredGroups.length && hasSearch ? (
            <Card bg={cardBg} borderWidth="1px" borderColor={borderColor}>
              <CardBody>
                <Heading size="sm">No matching Factors</Heading>
                <Text mt={2} color={mutedColor}>
                  No factor or school matches this search.
                </Text>
              </CardBody>
            </Card>
          ) : (
            <VStack align="stretch" spacing={3} aria-label={`${mode} Factor groups`}>
              {filteredGroups.map((group) => (
                <NetworkPairingFactorGroup
                  key={group.key}
                  group={group}
                  mode={mode}
                  networkId={networkId}
                />
              ))}
            </VStack>
          )}
        </>
      )}
    </VStack>
  );
};

export default NetworkReportPairingTab;
